import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  DC_WARDS,
  VIRGINIA_CITIES,
  VIRGINIA_COUNTIES,
  MARYLAND_CITIES,
  MARYLAND_COUNTIES,
} from '@/utils/locationData';

export function LocationSelector({ formData, setFormData, required = true }) {
  const handleLocationTypeChange = (e) => {
    const newType = e.target.value;
    setFormData({
      ...formData,
      location_type: newType,
      location_detail: '',
      ward_number: '',
      city_county: '',
      virginia_city: '',
      virginia_county: '',
      maryland_city: '',
      maryland_county: '',
    });
  };

  const handleWardChange = (e) => {
    const ward = e.target.value;
    setFormData({
      ...formData,
      ward_number: ward,
      location_detail: ward ? `Ward ${ward}` : '',
    });
  };

  const handleVirginiaCityChange = (e) => {
    const city = e.target.value;
    setFormData({
      ...formData,
      virginia_city: city,
      location_detail: city || formData.virginia_county || '',
    });
  };

  const handleVirginiaCountyChange = (e) => {
    const county = e.target.value;
    setFormData({
      ...formData,
      virginia_county: county,
      city_county: county,
    });
  };

  const handleMarylandCityChange = (e) => {
    const city = e.target.value;
    setFormData({
      ...formData,
      maryland_city: city,
      location_detail: city || formData.maryland_county || '',
    });
  };

  const handleMarylandCountyChange = (e) => {
    const county = e.target.value;
    setFormData({
      ...formData,
      maryland_county: county,
      city_county: county,
    });
  };

  const handleOtherCityChange = (e) => {
    const city = e.target.value;
    if (formData.location_type === 'Virginia') {
      setFormData({ ...formData, virginia_city_other: city, location_detail: city });
    } else {
      setFormData({ ...formData, maryland_city_other: city, location_detail: city });
    }
  };

  const handleOtherCountyChange = (e) => {
    const county = e.target.value;
    if (formData.location_type === 'Virginia') {
      setFormData({ ...formData, virginia_county_other: county, city_county: county });
    } else {
      setFormData({ ...formData, maryland_county_other: county, city_county: county });
    }
  };

  const selectClass = "w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white";

  return (
    <div className="space-y-4">
      {/* Location Type */}
      <div>
        <Label htmlFor="location_type">Location Type {required && '*'}</Label>
        <select
          id="location_type"
          data-testid="location-type"
          value={formData.location_type}
          onChange={handleLocationTypeChange}
          className={selectClass}
          required={required}
        >
          <option value="DC Ward">Washington, DC</option>
          <option value="Maryland">Maryland</option>
          <option value="Virginia">Virginia</option>
        </select>
      </div>

      {/* DC Ward Selection */}
      {formData.location_type === 'DC Ward' && (
        <div>
          <Label htmlFor="ward_number">DC Ward {required && '*'}</Label>
          <select
            id="ward_number"
            data-testid="ward-number"
            value={formData.ward_number || ''}
            onChange={handleWardChange}
            className={selectClass}
            required={required}
          >
            <option value="">Select Ward</option>
            {DC_WARDS.map(ward => (
              <option key={ward.value} value={ward.value}>{ward.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Virginia City and County Selection */}
      {formData.location_type === 'Virginia' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="virginia_city">City {required && '*'}</Label>
              <select
                id="virginia_city"
                data-testid="virginia-city"
                value={formData.virginia_city || ''}
                onChange={handleVirginiaCityChange}
                className={selectClass}
                required={required}
              >
                {VIRGINIA_CITIES.map(city => (
                  <option key={city.value} value={city.value}>{city.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="virginia_county">County {required && '*'}</Label>
              <select
                id="virginia_county"
                data-testid="virginia-county"
                value={formData.virginia_county || ''}
                onChange={handleVirginiaCountyChange}
                className={selectClass}
                required={required}
              >
                {VIRGINIA_COUNTIES.map(county => (
                  <option key={county.value} value={county.value}>{county.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Other City Input */}
          {formData.virginia_city === 'Other' && (
            <div>
              <Label htmlFor="virginia_city_other">Specify City {required && '*'}</Label>
              <Input
                id="virginia_city_other"
                data-testid="virginia-city-other"
                value={formData.virginia_city_other || ''}
                onChange={handleOtherCityChange}
                placeholder="Enter your city"
                required={required}
              />
            </div>
          )}
          
          {/* Other County Input */}
          {formData.virginia_county === 'Other' && (
            <div>
              <Label htmlFor="virginia_county_other">Specify County {required && '*'}</Label>
              <Input
                id="virginia_county_other"
                data-testid="virginia-county-other"
                value={formData.virginia_county_other || ''}
                onChange={handleOtherCountyChange}
                placeholder="Enter your county"
                required={required}
              />
            </div>
          )}
        </>
      )}

      {/* Maryland City and County Selection */}
      {formData.location_type === 'Maryland' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="maryland_city">City {required && '*'}</Label>
              <select
                id="maryland_city"
                data-testid="maryland-city"
                value={formData.maryland_city || ''}
                onChange={handleMarylandCityChange}
                className={selectClass}
                required={required}
              >
                {MARYLAND_CITIES.map(city => (
                  <option key={city.value} value={city.value}>{city.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="maryland_county">County {required && '*'}</Label>
              <select
                id="maryland_county"
                data-testid="maryland-county"
                value={formData.maryland_county || ''}
                onChange={handleMarylandCountyChange}
                className={selectClass}
                required={required}
              >
                {MARYLAND_COUNTIES.map(county => (
                  <option key={county.value} value={county.value}>{county.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Other City Input */}
          {formData.maryland_city === 'Other' && (
            <div>
              <Label htmlFor="maryland_city_other">Specify City {required && '*'}</Label>
              <Input
                id="maryland_city_other"
                data-testid="maryland-city-other"
                value={formData.maryland_city_other || ''}
                onChange={handleOtherCityChange}
                placeholder="Enter your city"
                required={required}
              />
            </div>
          )}
          
          {/* Other County Input */}
          {formData.maryland_county === 'Other' && (
            <div>
              <Label htmlFor="maryland_county_other">Specify County {required && '*'}</Label>
              <Input
                id="maryland_county_other"
                data-testid="maryland-county-other"
                value={formData.maryland_county_other || ''}
                onChange={handleOtherCountyChange}
                placeholder="Enter your county"
                required={required}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default LocationSelector;
